import { Form, FormInstance, Input, notification, Radio } from 'antd';
import React, { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import addBankAccount from '../../api/addBankAccount';
import getBankAccounts from '../../api/getBankAccounts';
import getHEOnboardingProfile, {
    BankDetails,
    HEOnboardingProfile,
} from '../../api/getHEOnboardingProfile';
import getValidationStatus from '../../api/getValidationStatus';
import markBankAccountValidated from '../../api/markBankAccountValidated';
import setHEOnboardingProfile from '../../api/setHEOnboardingProfile';
import { GO_TO_APPOINTMENTS, GO_TO_HE_ONBOARDING } from '../../routes/paths';
import { getSelectedClinicId } from '../../store/idbHandler';
import { track } from '../../tracking';
import { captureError, captureErrorString } from '../captureError';
import { HEOnboardingState, heOnboardingStepsArray } from './HEOnboarding';
import { FetchingLoader, Footer, Header } from './Utils';
import ValidatedBankDetails from './ValidatedBankDetails';

interface State {
    isFetchingProfile?: boolean;
    active_tab: string;
    bank_details?: BankDetails;
    isVerifyingBankDetails?: boolean;
    isSavingProfile?: boolean;
    isSkipAndNextLoading?: boolean;
    validatedBankDetails?: { account_number?: string; upi_id?: string; registered_name: string };
    fund_acc_id?: string;
}

type Props = RouteComponentProps;

class BankPage extends PureComponent<Props, State> {
    state: State = {
        isFetchingProfile: true,
        active_tab: 'bank_account',
    };
    polling?: NodeJS.Timeout = undefined;
    POLL_INTERVAL_MS = 10 * 1000;

    bankAccountFormRef: React.Ref<FormInstance<HEOnboardingProfile>> = React.createRef();
    upiIdFormRef: React.Ref<FormInstance<HEOnboardingProfile>> = React.createRef();
    currentIndex = 1;

    componentDidMount = async (): Promise<void> => {
        const { getAllBankAccounts } = this;
        try {
            const { success, profile, error = '' } = await getHEOnboardingProfile();
            if (success && profile) {
                const { bank_details = {} } = profile;
                this.setState({ bank_details });
                const { beneficiary_name, ifsc_code, account_number, upi_id } = bank_details;
                if (
                    !beneficiary_name?.trim() &&
                    !ifsc_code?.trim() &&
                    !String(account_number)?.trim() &&
                    !upi_id?.trim()
                ) {
                    getAllBankAccounts();
                }
            } else {
                getAllBankAccounts();
                captureErrorString(error);
                track('ERROR', { on: 'getHEOnboardingProfile', what: error });
            }
        } catch (error) {
            getAllBankAccounts();
            captureError(error as Error);
            track('ERROR', {
                on: 'getHEOnboardingProfile',
                error: (error as Error).message,
            });
        } finally {
            this.setState({ isFetchingProfile: false });
        }
    };

    getAllBankAccounts = async () => {
        try {
            const { success, response, error = '' } = await getBankAccounts();
            if (success && response && response[0]) {
                const { bank_account, vpa } = response[0];
                this.setState({
                    bank_details: {
                        beneficiary_name: bank_account?.name,
                        ifsc_code: bank_account?.ifsc,
                        account_number: bank_account?.account_number,
                        upi_id: vpa?.address,
                    },
                    active_tab: bank_account ? 'bank_account' : 'upi_id',
                });
            } else {
                captureErrorString(error);
                track('ERROR', { on: 'getBankAccounts', what: error });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getBankAccounts',
                error: (error as Error).message,
            });
        }
    };

    saveDetailsHandler = async (lastStep: HEOnboardingState, isConfirmed?: boolean) => {
        const { bank_details } = this.state;
        const bankAccountForm = ((this
            .bankAccountFormRef as unknown) as React.RefObject<HTMLFormElement>).current;
        const upiIdForm = ((this.upiIdFormRef as unknown) as React.RefObject<HTMLFormElement>)
            .current;

        this.setState({
            isSavingProfile: true,
        });

        let { fund_acc_id = '' } = this.state;
        if (!isConfirmed) fund_acc_id = '';

        if (bankAccountForm) {
            const {
                account_number,
                account_type,
                beneficiary_name,
                ifsc_code,
            } = await bankAccountForm.validateFields();
            if (fund_acc_id) {
                const { response } = await markBankAccountValidated(fund_acc_id);
                if (!response.success) {
                    notification.error({
                        message: 'Error while validating details!',
                        duration: 5,
                    });
                    return;
                }
            }
            this.setState({
                bank_details: {
                    ...bank_details,
                    account_number,
                    beneficiary_name,
                    ifsc_code,
                },
            });
            await setHEOnboardingProfile({
                bank_details: {
                    beneficiary_name,
                    ifsc_code,
                    account_number,
                    account_type,
                    fund_acc_id,
                },
                lastStep,
            });
            this.setState({
                isSavingProfile: false,
            });
        }

        if (upiIdForm) {
            const { upi_id } = await upiIdForm.validateFields();
            if (fund_acc_id) {
                const { response } = await markBankAccountValidated(fund_acc_id);
                if (!response.success) {
                    notification.error({
                        message: 'Error while validating details!',
                        duration: 5,
                    });
                    return;
                }
            }
            this.setState({
                bank_details: {
                    ...bank_details,
                    upi_id,
                },
            });
            await setHEOnboardingProfile({
                bank_details: {
                    fund_acc_id,
                    upi_id,
                },
                lastStep,
            });
            this.setState({
                isSavingProfile: false,
            });
        }
    };

    onClickHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex + 1], true);
            history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex + 1]));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    saveAndExitHandler = async () => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex } = this;

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex]);
            history.replace(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        }
    };

    onVerifyBankDetailsHandler = async () => {
        const { bank_details } = this.state;
        const bankAccountForm = ((this
            .bankAccountFormRef as unknown) as React.RefObject<HTMLFormElement>).current;
        const upiIdForm = ((this.upiIdFormRef as unknown) as React.RefObject<HTMLFormElement>)
            .current;

        try {
            if (bankAccountForm) {
                const {
                    account_number,
                    beneficiary_name,
                    ifsc_code,
                } = await bankAccountForm.validateFields();
                if (
                    !String(account_number)?.trim() ||
                    !beneficiary_name?.trim() ||
                    !ifsc_code?.trim()
                ) {
                    notification.error({
                        message: 'Please enter your bank details!',
                        duration: 5,
                    });
                    return;
                }
                this.setState({
                    bank_details: {
                        ...bank_details,
                        account_number,
                        beneficiary_name,
                        ifsc_code,
                    },
                    isVerifyingBankDetails: true,
                });
                const { success, response } = await addBankAccount({
                    account_type: 'bank_account',
                    account_details: {
                        name: beneficiary_name,
                        ifsc: ifsc_code,
                        account_number: account_number,
                    },
                });
                const { error } = response;
                if (success) {
                    const { fund_acc_id } = response;
                    if (fund_acc_id && account_number) {
                        this.poll(fund_acc_id, account_number);
                        this.polling = setInterval(
                            () => this.poll(fund_acc_id, account_number),
                            this.POLL_INTERVAL_MS,
                        );
                    }
                    track('BANK_DETAILS_VERIFIED', { fund_acc_id, account_number });
                } else {
                    notification.error({
                        message: error?.description || 'Error while validating bank details!',
                        duration: 5,
                    });
                    track('ERROR', {
                        on: 'verifyBankDetails',
                        error: error?.description,
                    });
                    this.setState({
                        isVerifyingBankDetails: false,
                    });
                }
            }

            if (upiIdForm) {
                const { upi_id } = await upiIdForm.validateFields();
                if (!upi_id?.trim()) {
                    notification.error({
                        message: 'Please enter your upi id details!',
                        duration: 5,
                    });
                    return;
                }
                this.setState({
                    bank_details: {
                        ...bank_details,
                        upi_id,
                    },
                    isVerifyingBankDetails: true,
                });
                const { success, response } = await addBankAccount({
                    account_type: 'vpa',
                    account_details: {
                        address: upi_id,
                    },
                });
                const { error } = response;
                if (success) {
                    const { fund_acc_id } = response;
                    if (fund_acc_id && upi_id) {
                        this.poll(fund_acc_id, undefined, upi_id);
                        this.polling = setInterval(
                            () => this.poll(fund_acc_id, undefined, upi_id),
                            this.POLL_INTERVAL_MS,
                        );
                    }
                    track('UPI_ID_DETAILS_VERIFIED', { fund_acc_id, upi_id });
                } else {
                    notification.error({
                        message: error?.description || 'Error while validating bank details!',
                        duration: 5,
                    });
                    track('ERROR', {
                        on: 'verifyUpiIdDetails',
                        error: error?.description,
                    });
                    this.setState({
                        isVerifyingBankDetails: false,
                    });
                }
            }
        } catch (err) {
            captureError(err as Error);
            notification.error({
                message: 'Error while validating bank details!',
                duration: 5,
            });
            this.setState({
                isVerifyingBankDetails: false,
            });
        }
    };

    poll = async (fund_acc_id: string, account_number?: string, upi_id?: string) => {
        try {
            const { response } = await getValidationStatus(fund_acc_id);
            const { validation_details } = response;
            const { registered_name } = validation_details;

            if (response.status === 'validation_completed' || response.status === 'validated') {
                if (this.polling) {
                    clearInterval(this.polling);
                }
                if (!registered_name) {
                    notification.error({
                        message: `Please enter valid ${
                            account_number ? 'bank account' : 'upi id'
                        } details!`,
                        duration: 5,
                    });
                    this.setState({
                        isVerifyingBankDetails: false,
                    });
                    return;
                }
                this.setState({
                    isVerifyingBankDetails: false,
                    validatedBankDetails: {
                        registered_name,
                        account_number,
                        upi_id,
                    },
                    fund_acc_id,
                });
            }
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'getValidationStatus',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while validating bank details!',
                duration: 5,
            });
        }
    };

    onBackHandler = (): void => {
        const { history } = this.props;
        const { currentIndex } = this;
        history.replace(GO_TO_HE_ONBOARDING(heOnboardingStepsArray[currentIndex - 1]));
    };

    onSkipAndStartHandler = async (): Promise<void> => {
        const { history } = this.props;
        const { saveDetailsHandler, currentIndex, activateInGenie } = this;

        this.setState({
            isSkipAndNextLoading: true,
        });

        try {
            await saveDetailsHandler(heOnboardingStepsArray[currentIndex]);
            await activateInGenie();
            history.replace(GO_TO_HE_ONBOARDING('finish'));
        } catch (err) {
            captureError(err as Error);
            track('ERROR', {
                on: 'setHEOnboardingProfile',
                error: (err as Error).message,
            });
            notification.error({
                message: 'Error while saving details!',
                duration: 5,
            });
        } finally {
            this.setState({
                isSkipAndNextLoading: false,
            });
        }
    };

    activateInGenie = async () => {
        try {
            const { profile } = await getHEOnboardingProfile();
            const { isPharmaEnabled, isLabEnabled } = profile;
            await setHEOnboardingProfile({
                isPharmaEnabled,
                isLabEnabled,
                isActivated: true,
                activateInProfile: true,
            });
            track('SET_HE_ONBOARDING_PROFILE', {
                isPharmaEnabled: String(isPharmaEnabled),
                isLabEnabled: String(isLabEnabled),
            });
            notification.success({
                message: 'Health exchange activated successfully!',
                duration: 5,
            });
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'activateInGenie',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Error while saving',
                duration: 5,
            });
        }
    };

    render(): ReactNode {
        const {
            isFetchingProfile,
            bank_details,
            isVerifyingBankDetails,
            isSavingProfile,
            validatedBankDetails,
            active_tab,
            isSkipAndNextLoading,
        } = this.state;
        const {
            onClickHandler,
            saveAndExitHandler,
            bankAccountFormRef,
            upiIdFormRef,
            onVerifyBankDetailsHandler,
            onBackHandler,
            onSkipAndStartHandler,
            currentIndex,
        } = this;

        const disabled = isVerifyingBankDetails || isSavingProfile || isSkipAndNextLoading;

        return (
            <Fragment>
                <FetchingLoader isFetchingProfile={isFetchingProfile} />
                <div className="flex flex-col h-full w-full justify-center">
                    <Header
                        saveAndExitHandler={saveAndExitHandler}
                        disabled={disabled}
                        isSavingProfile={isSavingProfile}
                    />

                    <div className="flex-1 overflow-scroll hide-scrollbar w-592 m-auto pb-32">
                        <div className="space-y-8 mb-24">
                            <p className="text-24 font-700">
                                Add your bank details to receive your earnings
                            </p>
                            <p className="text-darwin-neutral-900">
                                We will use this account for your earning settlements at end of
                                every payout cycle.
                            </p>
                        </div>
                        <div className="mb-16">
                            <Radio.Group
                                name="active_tab"
                                value={active_tab}
                                onChange={(e) => {
                                    this.setState({
                                        active_tab: e.target.value,
                                    });
                                }}
                            >
                                <Radio value={'bank_account'}>Bank Account</Radio>
                                <Radio value={'upi_id'}>UPI ID</Radio>
                            </Radio.Group>
                        </div>
                        {active_tab === 'bank_account' ? (
                            <div className="space-y-12">
                                <Form
                                    name="basic"
                                    initialValues={{ ...bank_details, account_type: 'saving' }}
                                    layout="vertical"
                                    ref={bankAccountFormRef}
                                    key={Math.random()}
                                >
                                    <Form.Item
                                        label="Account Number"
                                        name="account_number"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your Account Number!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="0000000000000" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Beneficiary / Bank Name"
                                        name="beneficiary_name"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please input your Beneficiary / Bank Name!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="Ex. Rahul Sharma" />
                                    </Form.Item>

                                    <Form.Item
                                        label="IFSC Code"
                                        name="ifsc_code"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your IFSC code!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="BANK00000000" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Account Type"
                                        name="account_type"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please select your Account type!',
                                            },
                                        ]}
                                    >
                                        <Radio.Group>
                                            <Radio.Button value="current">Current</Radio.Button>
                                            <Radio.Button value="saving">Saving</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                </Form>
                            </div>
                        ) : (
                            <div className="space-y-12">
                                <Form
                                    name="basic"
                                    initialValues={bank_details}
                                    layout="vertical"
                                    ref={upiIdFormRef}
                                    key={Math.random()}
                                >
                                    <Form.Item
                                        label="UPI ID"
                                        name="upi_id"
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your UPI ID!',
                                            },
                                        ]}
                                    >
                                        <Input placeholder="example@bank" />
                                    </Form.Item>
                                </Form>
                            </div>
                        )}
                    </div>

                    <Footer
                        onClickHandler={onVerifyBankDetailsHandler}
                        onBackHandler={onBackHandler}
                        disabled={disabled}
                        isSavingProfile={isVerifyingBankDetails}
                        currentIndex={currentIndex}
                        onSkipAndStartHandler={onSkipAndStartHandler}
                        isSkipAndNextLoading={isSkipAndNextLoading}
                    />
                </div>

                {validatedBankDetails && (
                    <ValidatedBankDetails
                        validatedBankDetails={validatedBankDetails}
                        onClose={() => this.setState({ validatedBankDetails: undefined })}
                        onConfirm={onClickHandler}
                        isSavingProfile={isSavingProfile}
                    />
                )}
            </Fragment>
        );
    }
}

export default connect()(withRouter(BankPage));
